<template>
  <div
    v-if="showWidget"
    id="knw_widget"
    ref="mainwidget"
    class="knw_widget"
    :class="{ isForcedFullscreen: isFullscreen }"
  >
    <!-- Main conversation window -->
    <transition-widget-conversation :alignment-origin="alignmentOrigin.x">
      <ChatPanel v-if="isWidgetOpen" key="WidgetElement" :frame="widgetFrame" />
    </transition-widget-conversation>

    <!-- Button -->
    <teleport :to="buttonPortalTarget">
      <transition-widget-button>
        <PortalButton v-show="!isWidgetOpen" :frame="buttonFrame" />
      </transition-widget-button>
    </teleport>

    <!-- Teaser -->
    <teleport :to="teaserPortalTarget">
      <!-- No transition here. We have three individual smaller ones
      within the teaser for every part of it -->
      <PortalTeaser v-show="!isWidgetOpen" :frame="teaserFrame" />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import ChatPanel from '@/components/widgetParts/chatPanel/ChatPanel.vue';
import PortalButton from '@/components/widgetParts/button/PortalButton.vue';
import PortalTeaser from '@/components/widgetParts/teaser/PortalTeaser.vue';
import TransitionWidgetButton from '@/transitions/transition-widget-button.vue';
import TransitionWidgetConversation from '@/transitions/transition-widget-conversation.vue';
import { useScrollLock } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onMounted, watch } from 'vue';
import { useCustomFont } from './composables/useCustomFont';
import { useLayoutStore } from './store/layout';
import { useThemeStore } from './store/theme';
import { useWidgetStore } from './store/widget';

const parentScrolllock = useScrollLock(window.parent.document.body);

const getIFrameFromParentById = (id: string) => {
  const iframe = (window.parent.document.getElementById(id));
  return iframe as HTMLIFrameElement;
};

const widgetStore = useWidgetStore();
const themeStore = useThemeStore();
const layoutStore = useLayoutStore();

// get the container element on the host site to apply zIndex
const containerElement = window.parent.document.getElementById('moin-ai-container');

// get all iframes for the widget, button and teaser to pass it to the components
const widgetFrame = getIFrameFromParentById('moin-ai-widget');
const buttonFrame = getIFrameFromParentById('moin-ai-button');
const teaserFrame = getIFrameFromParentById('moin-ai-teaser');


if (!widgetFrame || !buttonFrame || !teaserFrame) {
  throw new Error('Could not find all iframes for the widget, button and teaser');
}
// get the portal targets inner container elements for the teleports
const buttonPortalTarget = buttonFrame?.contentDocument?.getElementById('moinai-container');
const teaserPortalTarget = teaserFrame?.contentDocument?.getElementById('moinai-container');

const zIndex = computed(() => layoutStore.getZIndex);
const showWidget = computed(() => widgetStore.isVisible);
const getCustomFont = computed(() => themeStore.getCustomFont);
const isWidgetOpen = computed(() => widgetStore.isWidgetOpen);
const {
  isMobile,
  isFullscreen,
  alignmentOrigin
} = storeToRefs(layoutStore);

const applyCustomFonts = () => {
  if (!getCustomFont.value) return;
  const fonts = document.createElement('style');
  fonts.id = 'customfonts';
  useCustomFont(document, getCustomFont.value);
  useCustomFont(teaserFrame.contentDocument, getCustomFont.value);
  useCustomFont(buttonFrame.contentDocument, getCustomFont.value);
};

const updateScollLock = () => {
  if (isWidgetOpen.value && isMobile.value && widgetStore.isScrollLockAllowed) {
    parentScrolllock.value = true;
  } else {
    parentScrolllock.value = false;
  }
};

onBeforeMount(() => {
  applyCustomFonts();
});

onMounted(() => {
  updateScollLock();
});

watch(
  zIndex,
  () => {
    if (containerElement) {
      containerElement.style.zIndex = `${zIndex.value}`;
    }
  },
);

watch(
  getCustomFont,
  () => {
    applyCustomFonts();
  },
  { deep: true },
);

watch([isMobile, isWidgetOpen], () => {
  updateScollLock();
});
</script>

<style lang="scss">
/* rubik-regular - latin */
@use '@/styles/global.scss';

@font-face {
  font-family: 'Rubik';
  font-style: normal;
  font-weight: 400;
  src: url('fonts/rubik-v9-latin-regular.eot');

  src: local('Rubik'), local('Rubik-Regular'),
    url('fonts/rubik-v9-latin-regular.eot?#iefix') format('embedded-opentype'),
    url('fonts/rubik-v9-latin-regular.woff2') format('woff2'),
    url('fonts/rubik-v9-latin-regular.woff') format('woff'),
    url('fonts/rubik-v9-latin-regular.ttf') format('truetype'),
    url('fonts/rubik-v9-latin-regular.svg#Rubik') format('svg');

  font-display: swap;
}

/* rubik-500 - latin */
@font-face {
  font-family: 'Rubik';
  font-style: normal;
  font-weight: 500;
  src: url('fonts/rubik-v9-latin-500.eot');

  src: local('Rubik Medium'), local('Rubik-Medium'),
    url('fonts/rubik-v9-latin-500.eot?#iefix') format('embedded-opentype'),
    url('fonts/rubik-v9-latin-500.woff2') format('woff2'),
    url('fonts/rubik-v9-latin-500.woff') format('woff'),
    url('fonts/rubik-v9-latin-500.ttf') format('truetype'),
    url('fonts/rubik-v9-latin-500.svg#Rubik') format('svg');

  /* Legacy iOS */
  font-display: swap;
}



#knw_widget a:before {
  content: none;
}

#knw_widget {
  img {
    padding: 0;
  }
}

.knw_widget {
  box-sizing: border-box;
  pointer-events: none;
  @include default-font;
  // font: $kw-default-font;
  font-weight: 300;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 100%;
  max-width: 100%;
  height: 100dvh;
  min-height: -webkit-fill-available;
  min-height: 120px;
  z-index: var(--kw-widget-global-zindex);

  overflow: hidden;

  @media (min-width: 501px) {
    padding: 20px;
  }

  @media (min-width: $kw-widget-breakpoint) {
    overflow: visible;
    height: 100%;

    // DOES NOT WORK IN EDGE
    @supports (-ms-ime-align: auto) {
      height: 100%;
    }
  }
}

.knw_widget {
  &.isForcedFullscreen {
    padding: 0;

    .knw_widget_window {
      border-radius: 0;
    }
  }
}




// teaser transitions

// Button fade to background and bottom

/* .fade-leave-active below version 2.1.8 */
.teaser-hide-enter-from {
  opacity: 0;
  transform: rotate(0deg);
  transform-origin: bottom right;
}

.teaser-hide-enter-active {
  transition: all $kw-default-transition-speed;
}

/* .fade-leave-active below version 2.1.8 */
.teaser-hide-enter-to {
  opacity: 1;
  transform: rotate(0deg);
  transform-origin: bottom right;
}

// TEASER LEAVE
.teaser-hide-leave-from {
  opacity: 1;
  transform-origin: bottom right;

  .kw-teaser-closebutton-wrapper {
    opacity: 1;
  }

  .avatar-shadow-wrapper {
    opacity: 0;

    .avatar {
      transform: scale(1);
      opacity: 1;
    }
  }

  .kw-teaser-message {
    opacity: 1;
    transform: scale(1);
  }

  .kw-teaser-quickaction-wrapper {
    opacity: 1;
    transform: scale(1);
  }
}

// TEASER LEAVE ACTIVE
.teaser-hide-leave-active {
  transition: all 0.5s;
  transition-delay: 0s;

  .kw-teaser-closebutton-wrapper {
    transition: all 0.25s;
    transition-delay: 0s;
  }

  .avatar-shadow-wrapper {
    transition: all 0.5s;
    transition-delay: 0.15s;

    .avatar {
      transition: all 0.4s;
      transition-delay: 0.15s;
    }
  }

  .kw-teaser-message {
    transition: all 0.25s;
    transition-delay: 0s;
    transform-origin: 100% 100%;
  }

  .kw-teaser-quickaction-wrapper {
    transition: all 0.25s;
    transition-delay: 0.1s;
    transform-origin: 100% 0%;
  }
}

// TEASER LEAVE TO

/* .fade-leave-active below version 2.1.8 */
.teaser-hide-leave-to {
  opacity: 0;
  transform-origin: bottom right;

  .kw-teaser-closebutton-wrapper {
    opacity: 0;
  }

  .avatar-shadow-wrapper {
    opacity: 0;

    .avatar {
      transform: scale(0);
      opacity: 0;
    }
  }

  .kw-teaser-message {
    opacity: 0;
    transform: scale(0);
  }

  .kw-teaser-quickaction-wrapper {
    opacity: 0;
    transform: scale(0);
  }
}

@media print {
  #moinai-container {
    display: none;
  }
}
</style>
